// AGC: FarmProHub Forum Page
// AGC: Staker-only forum for discussions - all posts and comments stored on-chain

import React, { useState, useEffect, useCallback } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract, usePublicClient } from 'wagmi'
import { motion } from 'framer-motion'
import { MessageSquare, Plus, User, Clock, ArrowLeft, Send, Lock, Loader2 } from 'lucide-react'
import { useStaking } from '../hooks/useStaking'
import { useUserType } from '../hooks/useUserType'

interface ForumPost {
  id: number
  author: string
  title: string
  content: string
  timestamp: number
  commentCount: number
}

interface ForumComment {
  id: number
  author: string
  content: string
  timestamp: number
}

// AGC: Forum contract ABI (matches StakerForum.sol)
const FORUM_ABI = [
  {
    name: 'createPost',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'title', type: 'string' },
      { name: 'content', type: 'string' },
    ],
    outputs: [{ name: 'postId', type: 'uint256' }],
  },
  {
    name: 'createComment',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'postId', type: 'uint256' },
      { name: 'content', type: 'string' },
    ],
    outputs: [{ name: 'commentId', type: 'uint256' }],
  },
  {
    name: 'getPost',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'postId', type: 'uint256' }],
    outputs: [
      { name: 'author', type: 'address' },
      { name: 'title', type: 'string' },
      { name: 'content', type: 'string' },
      { name: 'timestamp', type: 'uint256' },
      { name: 'commentCountValue', type: 'uint256' },
      { name: 'deleted', type: 'bool' },
    ],
  },
  {
    name: 'postCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'getLatestPosts',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'offset', type: 'uint256' },
      { name: 'limit', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'uint256[]' }],
  },
  {
    name: 'getPostCommentIds',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'postId', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256[]' }],
  },
  {
    name: 'getComment',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'commentId', type: 'uint256' }],
    outputs: [
      { name: 'postId', type: 'uint256' },
      { name: 'author', type: 'address' },
      { name: 'content', type: 'string' },
      { name: 'timestamp', type: 'uint256' },
      { name: 'deleted', type: 'bool' },
    ],
  },
] as const

// AGC: Forum contract address (will be set when deployed)
const FORUM_CONTRACT_ADDRESS = import.meta.env.VITE_FORUM_ADDRESS as `0x${string}` | undefined

export const ForumPage: React.FC = () => {
  const { address, isConnected } = useAccount()
  const { stats } = useStaking()
  const userType = useUserType()
  const { writeContractAsync, isPending: isPosting } = useWriteContract()
  const [posts, setPosts] = useState<ForumPost[]>([])
  const [selectedPost, setSelectedPost] = useState<ForumPost | null>(null)
  const [comments, setComments] = useState<ForumComment[]>([])
  const [showNewPostModal, setShowNewPostModal] = useState(false)
  const [newPostTitle, setNewPostTitle] = useState('')
  const [newPostContent, setNewPostContent] = useState('')
  const [newComment, setNewComment] = useState('')
  const [isCommenting, setIsCommenting] = useState(false)
  const [isLoadingPosts, setIsLoadingPosts] = useState(false)
  const [createPostHash, setCreatePostHash] = useState<`0x${string}` | undefined>()

  // AGC: Use isStaker from hook (checks if user has any stake)
  const isStaker = stats.isStaker

  // AGC: Get public client for reading contracts
  const publicClient = usePublicClient()

  // AGC: Read post count from contract
  const { data: postCount, refetch: refetchPostCount } = useReadContract({
    address: FORUM_CONTRACT_ADDRESS,
    abi: FORUM_ABI,
    functionName: 'postCount',
    query: {
      enabled: !!FORUM_CONTRACT_ADDRESS,
      refetchInterval: 10000, // AGC: Refetch every 10 seconds
    },
  })

  // AGC: Wait for post creation transaction
  const { isLoading: isWaitingPost, isSuccess: postCreated } = useWaitForTransactionReceipt({
    hash: createPostHash,
  })

  // AGC: Load posts from contract
  const loadPosts = useCallback(async () => {
    if (!FORUM_CONTRACT_ADDRESS || !publicClient || !postCount || postCount === 0n) {
      setPosts([])
      return
    }

    try {
      setIsLoadingPosts(true)
      
      // AGC: Get latest post IDs (get last 50 posts)
      const postIds = await publicClient.readContract({
        address: FORUM_CONTRACT_ADDRESS,
        abi: FORUM_ABI,
        functionName: 'getLatestPosts',
        args: [0n, 50n],
      }) as bigint[]

      // AGC: Fetch each post
      const postsData: ForumPost[] = []
      for (const postId of postIds) {
        try {
          const postData = await publicClient.readContract({
            address: FORUM_CONTRACT_ADDRESS,
            abi: FORUM_ABI,
            functionName: 'getPost',
            args: [postId],
          }) as [string, string, string, bigint, bigint, boolean]

          const [author, title, content, timestamp, commentCountValue, deleted] = postData

          // AGC: Skip deleted posts
          if (deleted) continue

          postsData.push({
            id: Number(postId),
            author: author,
            title: title,
            content: content,
            timestamp: Number(timestamp),
            commentCount: Number(commentCountValue),
          })
        } catch (error) {
          console.error(`AGC: Error loading post ${postId}:`, error)
        }
      }

      // AGC: Sort by timestamp (newest first)
      postsData.sort((a, b) => b.timestamp - a.timestamp)
      setPosts(postsData)
    } catch (error) {
      console.error('AGC: Error loading posts:', error)
    } finally {
      setIsLoadingPosts(false)
    }
  }, [FORUM_CONTRACT_ADDRESS, publicClient, postCount])

  // AGC: Load comments for selected post
  const loadComments = useCallback(async (postId: number) => {
    if (!FORUM_CONTRACT_ADDRESS || !publicClient || !selectedPost) return

    try {
      // AGC: Get comment IDs for this post
      const commentIds = await publicClient.readContract({
        address: FORUM_CONTRACT_ADDRESS,
        abi: FORUM_ABI,
        functionName: 'getPostCommentIds',
        args: [BigInt(postId)],
      }) as bigint[]

      // AGC: Fetch each comment
      const commentsData: ForumComment[] = []
      for (const commentId of commentIds) {
        try {
          const commentData = await publicClient.readContract({
            address: FORUM_CONTRACT_ADDRESS,
            abi: FORUM_ABI,
            functionName: 'getComment',
            args: [commentId],
          }) as [bigint, string, string, bigint, boolean]

          const [_, author, content, timestamp, deleted] = commentData

          // AGC: Skip deleted comments
          if (deleted) continue

          commentsData.push({
            id: Number(commentId),
            author: author,
            content: content,
            timestamp: Number(timestamp),
          })
        } catch (error) {
          console.error(`AGC: Error loading comment ${commentId}:`, error)
        }
      }

      // AGC: Sort by timestamp (oldest first)
      commentsData.sort((a, b) => a.timestamp - b.timestamp)
      setComments(commentsData)
    } catch (error) {
      console.error('AGC: Error loading comments:', error)
    }
  }, [FORUM_CONTRACT_ADDRESS, publicClient, selectedPost])

  // AGC: Load posts when component mounts or postCount changes
  useEffect(() => {
    if (FORUM_CONTRACT_ADDRESS && publicClient && postCount !== undefined) {
      loadPosts()
    }
  }, [FORUM_CONTRACT_ADDRESS, publicClient, postCount, loadPosts])

  // AGC: Reload posts after post is created
  useEffect(() => {
    if (postCreated) {
      refetchPostCount()
      loadPosts()
      setCreatePostHash(undefined)
    }
  }, [postCreated])

  // AGC: Load comments when post is selected
  useEffect(() => {
    if (selectedPost && FORUM_CONTRACT_ADDRESS) {
      loadComments(selectedPost.id)
    }
  }, [selectedPost, loadComments, FORUM_CONTRACT_ADDRESS])

  // AGC: Format timestamp
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // AGC: Truncate address
  const truncateAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  // AGC: Handle create post (on-chain)
  const handleCreatePost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      alert('Please fill in title and content')
      return
    }

    if (!isStaker) {
      alert('You must be a staker to create posts')
      return
    }

    if (!FORUM_CONTRACT_ADDRESS) {
      alert('Forum contract not deployed yet. This is UI-only mode for testing.')
      // AGC: For UI testing, add mock post
      const mockPost: ForumPost = {
        id: Date.now(),
        author: address || '0x0000000000000000000000000000000000000000',
        title: newPostTitle,
        content: newPostContent,
        timestamp: Math.floor(Date.now() / 1000),
        commentCount: 0,
      }
      setPosts([mockPost, ...posts])
      setNewPostTitle('')
      setNewPostContent('')
      setShowNewPostModal(false)
      return
    }

    try {
      const hash = await writeContractAsync({
        address: FORUM_CONTRACT_ADDRESS,
        abi: FORUM_ABI,
        functionName: 'createPost',
        args: [newPostTitle, newPostContent],
      })

      // AGC: Store hash to wait for confirmation
      setCreatePostHash(hash)
      
      setNewPostTitle('')
      setNewPostContent('')
      setShowNewPostModal(false)
      
      // AGC: Posts will reload automatically after transaction confirms (via useEffect)
    } catch (error) {
      console.error('AGC: Error creating post:', error)
      alert(`Failed to create post: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // AGC: Handle add comment (on-chain)
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert('Please enter a comment')
      return
    }

    if (!selectedPost) return

    if (!isStaker) {
      alert('You must be a staker to comment')
      return
    }

    if (!FORUM_CONTRACT_ADDRESS) {
      alert('Forum contract not deployed yet. This is UI-only mode for testing.')
      // AGC: For UI testing, add mock comment
      const mockComment: ForumComment = {
        id: Date.now(),
        author: address || '0x0000000000000000000000000000000000000000',
        content: newComment,
        timestamp: Math.floor(Date.now() / 1000),
      }
      setComments([...comments, mockComment])
      setNewComment('')
      return
    }

    try {
      setIsCommenting(true)
      const hash = await writeContractAsync({
        address: FORUM_CONTRACT_ADDRESS,
        abi: FORUM_ABI,
        functionName: 'createComment',
        args: [BigInt(selectedPost.id), newComment],
      })

      // AGC: Wait for transaction and reload comments
      if (publicClient) {
        await publicClient.waitForTransactionReceipt({ hash })
      }

      setNewComment('')
      
      // AGC: Reload comments after transaction confirms
      if (selectedPost) {
        await loadComments(selectedPost.id)
      }
    } catch (error) {
      console.error('AGC: Error adding comment:', error)
      alert(`Failed to add comment: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsCommenting(false)
    }
  }

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4 relative"
        >
          {/* AGC: User Type Badge - Top Right */}
          {isConnected && (
            <div className="absolute top-0 right-0">
              <div className={`${userType.bgColor} ${userType.borderColor} border rounded-lg px-3 py-1.5 flex items-center gap-2`}>
                <span className={`text-xs font-semibold ${userType.color}`}>
                  Type: {userType.label}
                </span>
              </div>
            </div>
          )}
          <h1 className={`text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2 ${isConnected ? 'pt-10' : ''}`}>
            <MessageSquare className="w-7 h-7 text-orange-400" />
            Staker Forum
          </h1>
          <p className="text-slate-400 text-sm">
            Discuss ideas and proposals with fellow stakers
          </p>
        </motion.div>

        {/* AGC: Staker Check */}
        {!isStaker && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-yellow-900/30 border border-yellow-500/30 rounded-xl p-5 text-center"
          >
            <Lock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Stakers Only</h3>
            <p className="text-slate-400 text-sm">
              {isConnected 
                ? 'You need to stake FP tokens to participate in the forum.'
                : 'Connect your wallet and stake FP tokens to participate in the forum.'}
            </p>
            <p className="text-slate-500 text-xs mt-2">
              You can still read posts, but posting and commenting requires staking.
            </p>
          </motion.div>
        )}

        {/* AGC: Post List or Selected Post */}
        {!selectedPost ? (
          <>
            {/* AGC: New Post Button */}
            {isStaker && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <button
                  onClick={() => setShowNewPostModal(true)}
                  className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create New Post
                </button>
              </motion.div>
            )}

            {/* AGC: Posts List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold">Recent Posts</h3>
                {isLoadingPosts && (
                  <Loader2 className="w-4 h-4 text-orange-400 animate-spin" />
                )}
              </div>
              
              {isLoadingPosts && posts.length === 0 ? (
                <div className="bg-slate-800/50 rounded-xl p-8 border border-slate-700/50 text-center">
                  <Loader2 className="w-12 h-12 text-orange-400 mx-auto mb-4 animate-spin" />
                  <h4 className="text-white font-semibold mb-2">Loading Posts...</h4>
                  <p className="text-slate-400 text-sm">
                    Fetching posts from blockchain...
                  </p>
                </div>
              ) : posts.length === 0 ? (
                <div className="bg-slate-800/50 rounded-xl p-8 border border-slate-700/50 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h4 className="text-white font-semibold mb-2">No Posts Yet</h4>
                  <p className="text-slate-400 text-sm">
                    Be the first to start a discussion!
                  </p>
                </div>
              ) : (
                posts.map((post) => (
                  <div
                    key={post.id}
                    onClick={() => setSelectedPost(post)}
                    className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-orange-500/30 transition-colors cursor-pointer"
                  >
                    <h4 className="text-white font-semibold mb-2">{post.title}</h4>
                    <p className="text-slate-400 text-sm line-clamp-2 mb-3">
                      {post.content}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3" />
                        <span>{truncateAddress(post.author)}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {post.commentCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(post.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          </>
        ) : (
          // AGC: Selected Post View
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* AGC: Back Button */}
            <button
              onClick={() => setSelectedPost(null)}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Posts
            </button>

            {/* AGC: Post Content */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h2 className="text-xl font-bold text-white mb-4">{selectedPost.title}</h2>
              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {truncateAddress(selectedPost.author)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatTime(selectedPost.timestamp)}
                </span>
              </div>
              <p className="text-slate-300 whitespace-pre-wrap">{selectedPost.content}</p>
            </div>

            {/* AGC: Comments Section */}
            <div className="space-y-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-orange-400" />
                Comments ({comments.length})
              </h3>

              {/* AGC: Comment Input */}
              {isStaker && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || isCommenting}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCommenting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Commenting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Comment (On-Chain)
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* AGC: Comments List */}
              {comments.length === 0 ? (
                <div className="bg-slate-800/30 rounded-xl p-4 text-center">
                  <p className="text-slate-500 text-sm">No comments yet</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {truncateAddress(comment.author)}
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(comment.timestamp)}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm">{comment.content}</p>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* AGC: New Post Modal */}
        {showNewPostModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-800 rounded-xl p-6 w-full max-w-lg"
            >
              <h3 className="text-xl font-bold text-white mb-4">Create New Post</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Title</label>
                  <input
                    type="text"
                    value={newPostTitle}
                    onChange={(e) => setNewPostTitle(e.target.value)}
                    placeholder="Enter post title..."
                    maxLength={200}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Content</label>
                  <textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder="What's on your mind?"
                    maxLength={5000}
                    rows={6}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowNewPostModal(false)}
                  className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePost}
                  disabled={!newPostTitle.trim() || !newPostContent.trim() || isPosting || isWaitingPost}
                  className="flex-1 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isPosting || isWaitingPost ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isPosting ? 'Posting...' : 'Confirming...'}
                    </>
                  ) : (
                    'Post (On-Chain)'
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* AGC: Forum Guidelines */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-3">Forum Guidelines</h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-orange-400">•</span>
              Only stakers can create posts and comments
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-400">•</span>
              Posts and comments are stored on-chain (gas required) - All data is permanent and verifiable
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-400">•</span>
              Be respectful and constructive in discussions
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-400">•</span>
              Discuss proposals, ideas, and project improvements
            </li>
          </ul>
        </motion.div>
      </div>
    </main>
  )
}

